// API 服务
import axios from 'axios';

// 在生产环境中使用相对路径（即当前域名），开发环境中使用localhost
const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:8000' : window.location.origin;
export const isLocalhostAccess = () => ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // 发送 cookies
});

// 请求拦截器 - 添加认证头
api.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误等
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isLocalhostAccess()) {
      // 未授权，清除认证状态
      console.log('未授权，需要重新登录');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      // 只在非登录页面时跳转，避免登录页面无限刷新
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && !currentPath.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const recordPageVisit = (path) => api.post('/api/page-visits', { path });
export const getAuditLogs = (params) => api.get('/api/audit-logs', { params });
export const getAuditDetail = (id) => api.get(`/api/audit-logs/${id}`);

// 日志相关API
export const getLogTrend = () => api.get('/api/logs/trend');
export const getLogs = (params) => api.get('/api/logs', { params });
export const getLogDetail = (id) => api.get(`/api/logs/${id}`);
export const retryLog = (id) => api.post(`/api/logs/${id}/retry`);
export const triggerManual = (data) => api.post('/api/logs/trigger', data);
export const previewQueryTrigger = (data) => api.post('/api/logs/query-preview', data);
export const triggerQueryNow = (data) => api.post('/api/logs/query-trigger', data);
export const getQueryTriggerTasks = () => api.get('/api/logs/query-trigger-tasks');
export const createQueryTriggerTask = (data) => api.post('/api/logs/query-trigger-tasks', data);
export const updateQueryTriggerTask = (id, data) => api.put(`/api/logs/query-trigger-tasks/${id}`, data);
export const deleteQueryTriggerTask = (id) => api.delete(`/api/logs/query-trigger-tasks/${id}`);
export const cancelRunningQueryTriggerTask = (id) => api.post(`/api/logs/query-trigger-tasks/${id}/cancel-running`);
export const cancelPendingQueryTriggers = () => api.post('/api/logs/query-triggers/cancel-pending');
export const getJiraConfig = () => api.get('/api/config/jira');
export const getSystemJiraConfig = () => api.get('/api/system-config/jira');
export const updateSystemJiraConfig = (data) => api.put('/api/system-config/jira', data);
export const getKAStatus = () => api.get('/api/ka/status');
export const getKAStageDuration = params => api.get('/api/ka/stage-duration', { params, paramsSerializer: { indexes: null } });
export const getKADashboard = (params) => api.get('/api/ka/dashboard', { params, paramsSerializer: { indexes: null } });
export const getKAIssues = (params) => api.get('/api/ka/issues', { params, paramsSerializer: { indexes: null } });
export const exportKAIssues = (params) => api.get('/api/ka/issues/export', { params, paramsSerializer: { indexes: null }, responseType: 'blob' });
export const getKAMerchants = () => api.get('/api/ka/merchants');
export const createKAMerchant = (data) => api.post('/api/ka/merchants', data);
export const updateKAMerchant = (id, data) => api.put(`/api/ka/merchants/${id}`, data);
export const deleteKAMerchant = (id) => api.delete(`/api/ka/merchants/${id}`);
export const getKASettings = () => api.get('/api/ka/settings');
export const updateKASettings = (data) => api.put('/api/ka/settings', data);
export const syncKA = () => api.post('/api/ka/sync');
export const rebuildKA = () => api.post('/api/ka/rebuild');
export const getKAPending = (params) => api.get('/api/ka/pending', { params });
export const getKAOverrides = params => api.get('/api/ka/overrides', { params });
export const updateKAOverride = (issueKey, data) => api.put(`/api/ka/overrides/${encodeURIComponent(issueKey)}`, data);
export const batchUpdateKAOverrides = (data) => api.put('/api/ka/overrides/batch', data);
export const getKAProductDomains = () => api.get('/api/ka/product-domains');
export const saveKAProductDomain = (id, data) => id ? api.put(`/api/ka/product-domains/${id}`, data) : api.post('/api/ka/product-domains', data);
export const assignKAModuleDomains = (data) => api.put('/api/ka/module-domains', data);
export const updateKAModuleSource = (field_id) => api.put('/api/ka/module-source', { field_id });
export const getKASalesforceCustomers = params => api.get('/api/ka/salesforce/customers', { params });
export const lookupKASalesforceHandle = handle => api.get('/api/ka/salesforce/handle-lookup', { params: { handle } });
export const getKASalesforceReviews = params => api.get('/api/ka/salesforce/reviews', { params });
export const createKASalesforceMerchant = payload => api.post('/api/ka/salesforce/customers/create-merchant', payload);
export const getKASalesforceCustomerIssues = (id, params) => api.get(`/api/ka/salesforce/customers/${encodeURIComponent(id)}/issues`, { params });
export const syncKASalesforceDirectory = () => api.post('/api/ka/salesforce/sync', { kind: 'directory' });
export const previewKAMerchantMerge = data => api.post('/api/ka/merchants/merge-preview', data);
export const mergeKAMerchants = data => api.post('/api/ka/merchants/merge', data);
export const getKASalesforceSettings = () => api.get('/api/ka/salesforce/settings');
export const saveKASalesforceSettings = data => api.put('/api/ka/salesforce/settings', data);
export const unbindKASalesforce = (id, account_id) => api.delete(`/api/ka/merchants/${id}/salesforce`, { params: account_id ? { account_id } : {} });
export const bindKASalesforce = (id, account_id) => api.put(`/api/ka/merchants/${id}/salesforce`, { account_id });
export const getKAFields = () => api.get('/api/ka/fields');

// 规则相关API
export const getRules = () => api.get('/api/rules');
export const getRule = (id) => api.get(`/api/rules/${id}`);
export const createRule = (data) => api.post('/api/rules', data);
export const updateRule = (id, data) => api.put(`/api/rules/${id}`, data);
export const deleteRule = (id) => api.delete(`/api/rules/${id}`);
export const updateRuleStatus = (id) => 
  api.post(`/api/rules/${id}/toggle`);

// 规则组相关API
export const getGroups = () => api.get('/api/groups');
export const reorderGroups = (groupOrders) => api.post('/api/groups/reorder', { group_orders: groupOrders });
export const getGroup = (id) => api.get(`/api/groups/${id}`);
export const createGroup = (data) => api.post('/api/groups', data);
export const updateGroup = (id, data) => api.put(`/api/groups/${id}`, data);
export const deleteGroup = (id) => api.delete(`/api/groups/${id}`);
export const getGroupDelayPolicy = (id) => api.get(`/api/groups/${id}/delay-policy`);
export const updateGroupDelayPolicy = (id, data) => api.put(`/api/groups/${id}/delay-policy`, data);

// 工作日历相关API
export const getCalendars = () => api.get('/api/calendars');
export const createCalendar = (data) => api.post('/api/calendars', data);
export const updateCalendar = (id, data) => api.put(`/api/calendars/${id}`, data);
export const deleteCalendar = (id) => api.delete(`/api/calendars/${id}`);

// 字段相关API
export const getFields = () => api.get('/api/fields');
export const getField = (id) => api.get(`/api/fields/${id}`);
export const getFieldOptions = (fieldKey) => api.get(`/api/fields/${encodeURIComponent(fieldKey)}/options`);
export const getJiraProjectFields = (projectKey) => api.get('/api/fields/jira-project-fields', { params: { project_key: projectKey } });
export const syncJiraField = (data) => api.post('/api/fields/sync-jira', data);
export const createField = (data) => api.post('/api/fields', data);
export const updateField = (id, data) => api.put(`/api/fields/${id}`, data);
export const deleteField = (id) => api.delete(`/api/fields/${id}`);

// 人员相关API
export const getPersons = () => api.get('/api/persons');
export const getPerson = (id) => api.get(`/api/persons/${id}`);
export const createPerson = (data) => api.post('/api/persons', data);
export const updatePerson = (id, data) => api.put(`/api/persons/${id}`, data);
export const updatePersonStatus = (id, status) => 
  api.patch(`/api/persons/${id}/status`, { is_active: status });
export const deletePerson = (id) => api.delete(`/api/persons/${id}`);

// AI配置相关API
export const getWecomAgents = () => api.get('/api/ai/wecom-agents');
export const getRuleAIChannels = () => api.get('/api/ai/rule-channels');
export const getAIConfigs = () => api.get('/api/ai/configs');
export const getAIConfig = (provider) => api.get(`/api/ai/config/${provider}`);
export const updateAIConfig = (provider, data) => 
  api.post(`/api/ai/config/${provider}`, data);
export const updateAllAIConfigs = (data) => 
  api.post('/api/ai/configs', data);
export const getAgentConfigs = () => api.get('/api/ai/agent-configs');
export const createAgentConfig = (data) => api.post('/api/ai/agent-configs', data);
export const updateAgentConfig = (id, data) => api.put(`/api/ai/agent-configs/${id}`, data);
export const deleteAgentConfig = (id) => api.delete(`/api/ai/agent-configs/${id}`);
export const testAIChat = (data) => api.post('/api/ai/test-chat', data);

// 企业微信相关API
export const getWecomConfig = () => api.get('/api/wecom/config');
export const getWecomConfigs = () => api.get('/api/wecom/configs');
export const getWecomBindings = () => api.get('/api/wecom/bindings');
export const getWecomRuntimeStatuses = () => api.get('/api/wecom/runtime-status');
export const getWecomNotifications = (params) => api.get('/api/wecom/notifications', { params });
export const getWecomNotification = (id) => api.get(`/api/wecom/notifications/${id}`);
export const getWecomChatLogs = (params) => api.get('/api/wecom/chat-logs', { params });
export const getWecomChatConversations = (params) =>
  api.get('/api/wecom/chat-conversations', { params });
export const getWecomChatConversationLogs = (conversationId, params) =>
  api.get(
    `/api/wecom/chat-conversations/${encodeURIComponent(conversationId)}/logs`,
    { params }
  );
export const updateWecomConfig = (data) => api.put('/api/wecom/config', data);
export const testWecomConfig = (params) => api.post('/api/wecom/config/test', null, { params });
export const sendWecomNotification = (data) => api.post('/api/wecom/notifications/send', data);

// 状态相关API
export const getStatuses = () => api.get('/api/statuses');
export const getAvailableTransitions = (issueKey) => api.get(`/api/transitions/${issueKey}`);
export const getAvailableTransitionsLegacy = (issueKey) => api.get(`/api/statuses/${issueKey}`);

// 认证相关API
export const register = (data) => api.post('/api/auth/register', data);
export const login = (data) => api.post('/api/auth/login', data);
export const getGoogleLoginConfig = () => api.get('/api/auth/google/config');
export const googleLogin = (credential) => api.post('/api/auth/google', { credential });
export const logout = () => api.post('/api/auth/logout');
export const getCurrentUser = () => api.get('/api/auth/me');
export const updateJiraConfig = (data) => api.put('/api/auth/me/jira-config', data);
export const getMyJiraConfig = () => api.get('/api/auth/me/jira-config');
export const changePassword = (data) => api.post('/api/auth/me/change-password', data);
export const getUsers = () => api.get('/api/auth/users');
export const createUser = (data) => api.post('/api/auth/users', data);
export const deleteUser = (id) => api.delete(`/api/auth/users/${id}`);
export const updateUser = (id, data) => api.put(`/api/auth/users/${id}`, data);
export const updateUserStatus = (id, isActive) => api.put(`/api/auth/users/${id}/status`, null, { params: { is_active: isActive } });
export const resetUserPassword = (id, data) => api.post(`/api/auth/users/${id}/password`, data);
export const getPermissionCatalog = () => api.get('/api/auth/permission-catalog');
export const getPermissions = () => api.get('/api/auth/permissions');
export const getUserGroups = () => api.get('/api/auth/groups');
export const createUserGroup = (data) => api.post('/api/auth/groups', data);
export const updateUserGroup = (id, data) => api.put(`/api/auth/groups/${id}`, data, { timeout: 30000 });
export const deleteUserGroup = (id) => api.delete(`/api/auth/groups/${id}`);


const apis = {
  // 认证
  register,
  login,
  getGoogleLoginConfig,
  googleLogin,
  logout,
  getCurrentUser,
  updateJiraConfig,
  getMyJiraConfig,
  changePassword,
  getUsers,
  createUser,
  updateUser,
  updateUserStatus,
  resetUserPassword,
  getPermissions,
  getPermissionCatalog,
  getUserGroups,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
  deleteUser,
  
  // 日志
  getLogs,
  getLogTrend,
  getLogDetail,
  retryLog,
  triggerManual,
  previewQueryTrigger,
  triggerQueryNow,
  getQueryTriggerTasks,
  createQueryTriggerTask,
  updateQueryTriggerTask,
  deleteQueryTriggerTask,
  cancelRunningQueryTriggerTask,
  cancelPendingQueryTriggers,
  getJiraConfig,
  getSystemJiraConfig,
  updateSystemJiraConfig,
  getKAStatus,
  getKADashboard,
  getKAIssues,
  exportKAIssues,
  getKAMerchants,
  createKAMerchant,
  updateKAMerchant,
  deleteKAMerchant,
  getKASettings,
  updateKASettings,
  syncKA,
  rebuildKA,
  getKAPending,
  getKAOverrides,
  updateKAOverride,
  batchUpdateKAOverrides,
  getKAFields,
  getKASalesforceSettings,
  getKASalesforceCustomers,
  getKASalesforceReviews,
  syncKASalesforceDirectory,
  createKASalesforceMerchant,
  getKASalesforceCustomerIssues,
  previewKAMerchantMerge,
  mergeKAMerchants,
  saveKASalesforceSettings,
  bindKASalesforce,
  unbindKASalesforce,
  getKAProductDomains,
  saveKAProductDomain,
  assignKAModuleDomains,
  updateKAModuleSource,
  
  // 规则
  getRules,
  getRule,
  createRule,
  updateRule,
  deleteRule,
  updateRuleStatus,
  
  // 规则组
  getGroups,
  reorderGroups,
  getGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupDelayPolicy,
  updateGroupDelayPolicy,

  // 工作日历
  getCalendars,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  
  // 字段
  getFields,
  getField,
  getFieldOptions,
  getJiraProjectFields,
  syncJiraField,
  createField,
  updateField,
  deleteField,
  
  // 人员
  getPersons,
  getPerson,
  createPerson,
  updatePerson,
  updatePersonStatus,
  deletePerson,
  
  // AI配置
  getAIConfigs,
  getRuleAIChannels,
  getWecomAgents,
  getAIConfig,
  updateAIConfig,
  updateAllAIConfigs,
  getAgentConfigs,
  createAgentConfig,
  updateAgentConfig,
  deleteAgentConfig,
  testAIChat,

  // 企业微信
  getWecomConfig,
  getWecomConfigs,
  getWecomBindings,
  getWecomRuntimeStatuses,
  getWecomNotifications,
  getWecomNotification,
  getWecomChatLogs,
  getWecomChatConversations,
  getWecomChatConversationLogs,
  updateWecomConfig,
  testWecomConfig,
  sendWecomNotification,
  
  // 状态
  getStatuses,
  getAvailableTransitions,
  getAvailableTransitionsLegacy,
};

export default apis;
